import React, { useEffect, useRef, useState } from 'react';
import LeftIcon from '../../images/LeftIcon';
import { webLoadingFalse, webLoadingTrue, webNavigate } from '../../services/navigate.service';
import { validateNoSpecialChars, validateOnlyNumbersNotThree, } from '../../validation/ValidationInput';
import { createFloorPlan, FloorPlanDetail, FloorPlanDetailPayload, FloorPlanPayload, getDetailFloorPlan, updateFloorPlan, uploadFile } from '../../services/floorPlan.service';
import { getListSalePointCheckList, getLocations } from '../../services/locations.services';
import UploadIcon from '../../images/UploadIcon';
import CloseIcon from '../../images/CloseIcon';
import ClickIcon from '../../images/ClickIcon';
import DrawFloorPlan, { ImageShape } from './DrawFloorPlan';
import { showToast } from '../../services/toast.service';
import { showApiError } from '../../services/apiError';
import { inventoryFormLimits, limitProps, useFieldLimits, validateAgainstLimits } from '../../services/fieldLimits';
import FieldCounter from '../../components/FieldCounter';
import BackIcon from '../../images/BackIcon';
import UploadIconV2 from '../../images/UploadIconV2';
import PlusIcon from '../../images/PlusIcon';
import BarIcon from '../../images/BarIcon';
import KitchenIcon from '../../images/KitchenIcon';
import DiningIcon from '../../images/DiningIcon';
import { DropdownSearch } from '../../components/DropdownSearch';
import { useResponsive } from '../../hook/useResponsive';

export interface CountryResponse {
    name: string;
    id: string;
    isSelect: boolean;
}

const FloorPlan = () => {
    const [language, setLanguage] = useState<any>(null);
    const { isMobile, getWidth } = useResponsive();
    const [errors, setErrors] = useState<{ [key: string]: string }>({});
    const limits = useFieldLimits(inventoryFormLimits('floor-plan'));
    const path = window.location.pathname;
    const id: string = path.split('/').pop() || '';
    const preLanguage = localStorage.getItem('language');
    const userId = localStorage.getItem('userId') || '';
    const companyId = localStorage.getItem('companyId') || '';
    const [country, setCountry] = useState<CountryResponse[]>([]);
    const [salePoint, setSalePoint] = useState<any[]>([]);
    const dropdownCountryRef = useRef<HTMLDivElement>(null);
    const [isCountryDropdownOpen, setIsCountryDropdownOpen] = useState(false);
    const [searchCountry, setSearchCountry] = useState("");
    const [base64Image, setBase64Image] = useState<any>('');
    const [base64PDF, setBase64PDF] = useState<any>('');
    const [fileUpload, setFileUpload] = useState<any>('');
    const [showPopupImage, setShowPopupImage] = useState<boolean>(false);
    const [showPopupDraw, setShowPopupDraw] = useState<boolean>(false);
    const dropdownSalePointRef = useRef<HTMLDivElement>(null);
    const [isSalePointDropdownOpen, setIsSalePointDropdownOpen] = useState(false);
    const [searchSalePoint, setSearchSalePoint] = useState("");
    // FIN-1115: "Enable covers" boolean dropdown. Mirrors the location /
    // sale-point pattern (ref + open flag + display string). The display
    // string is kept in sync with formValues.coversEnabled via the effect
    // below so it survives both first-load language hydration and an
    // edit-flow fetchData() prefill.
    const dropdownCoversRef = useRef<HTMLDivElement>(null);
    const [isCoversDropdownOpen, setIsCoversDropdownOpen] = useState(false);
    const [searchCovers, setSearchCovers] = useState("");
    const [fileName, setFileName] = useState('');
    const [isDragging, setIsDragging] = useState(false);
    const [formValues, setFormValues] = useState({
        locationId: "",
        planName: "",
        position: 0,
        image: "",
        description: "",
        type: "bar",
        salePointFloorPlans: [{ salePointId: "", position: 0 }],
        floorPlanDetails: [] as FloorPlanDetailPayload[],
        isActive: false,
        // FIN-1115: defaults to false; server (FIN-1112) accepts both
        // boolean and null but treats undefined as false on create.
        coversEnabled: false as boolean,
    });

    // FIN-1115: static option list for the Enable covers dropdown. Built
    // inside the component so it picks up `language` for translated labels.
    const coversYesLabel = (language && language?.['Yes']) ? language['Yes'] : 'Yes';
    const coversNoLabel = (language && language?.['No']) ? language['No'] : 'No';
    const coversOptions = [
        { id: 'no', value: false, name: coversNoLabel },
        { id: 'yes', value: true, name: coversYesLabel },
    ];


    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownCountryRef.current && !dropdownCountryRef.current.contains(event.target as Node)) {
                setIsCountryDropdownOpen(false);
            }
            if (dropdownSalePointRef.current && !dropdownSalePointRef.current.contains(event.target as Node)) {
                setIsSalePointDropdownOpen(false);
            }
            // FIN-1115: same outside-click teardown for the Enable covers dropdown.
            if (dropdownCoversRef.current && !dropdownCoversRef.current.contains(event.target as Node)) {
                setIsCoversDropdownOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.addEventListener('mousedown', handleClickOutside);
    }, []);

    // FIN-1115: keep the Enable covers display string in sync with the
    // backing boolean. Runs on language hydration (so the initial "No"
    // becomes localized once language loads) and on edit-flow fetchData()
    // when formValues.coversEnabled flips to the persisted value.
    useEffect(() => {
        setSearchCovers(formValues.coversEnabled ? coversYesLabel : coversNoLabel);
    }, [language, formValues.coversEnabled]);
    useEffect(() => {
        if (window.languageContext) {
            setLanguage(window.languageContext.language);
        }
    }, [window, window.languageContext]);

    useEffect(() => {
        getSalePoints();
        if (id !== 'new') {
            fetchData();
        } else {
            getListCountry();
        }
        const handleNewToken = async () => {
            getSalePoints();
            if (id !== 'new') {
                fetchData();
            } else {
                getListCountry();
            }
        };
        document.addEventListener('newToken', handleNewToken);
        return () => {
            document.removeEventListener('newToken', handleNewToken);
        };
    }, [path]);


    const fetchData = async () => {
        webLoadingTrue();
        try {
            const response = await getDetailFloorPlan(id, localStorage.getItem('locationId') || '');
            const responseCountry: any = await getLocations(userId, companyId, '', '', '');
            const listCountry: CountryResponse[] = responseCountry.data.data;
            setCountry(listCountry);
            const data = response.data.data;
            setFormValues({
                type: data.type || "bar",
                isActive: data.isActive || false,
                locationId: data.locationId || "",
                planName: data.planName || "",
                position: data.position || 0,
                image: data.image || "",
                description: data.description || "",
                // FIN-1115: explicit boolean coercion. Backend may return
                // null (column is nullable) — treat that as false in the UI
                // since the dropdown only models a true/false choice.
                coversEnabled: data.coversEnabled === true,
                salePointFloorPlans: [{ salePointId: data.salePointFloorPlan[0].salePoint.id || "", position: data.salePointFloorPlan[0].salePoint.position || 1 }],
                floorPlanDetails: data?.floorPlanDetails.map((v: any) => ({
                    ...v,
                    x: Math.round((v.x / 100) * window.innerWidth),
                    y: Math.round((v.y / 100) * window.innerHeight),
                    width: Math.round((v.width / 100) * window.innerWidth),
                    height: Math.round((v.height / 100) * window.innerHeight),
                })),
            });
            setSearchCountry(listCountry.find((c) => c.id === data.locationId)?.name || "");
            setSearchSalePoint(data.salePointFloorPlan[0].salePoint.salePointName || "");
        } catch (error) {
            console.error('Error loading terms:', error);
        } finally {
            webLoadingFalse();
        }
    };

    const getSalePoints = async () => {
        try {
            const response: any = await getListSalePointCheckList(companyId, "0", 0, "");
            setSalePoint(response.data.data.salePoints);
        } catch (error) {
            console.error("Error loading terms:", error);
        }
    }

    const getListCountry = async () => {
        try {
            const response: any = await getLocations(userId, companyId, '', '', '');
            const listCountry: CountryResponse[] = response.data.data;
            setCountry(listCountry);
        } catch (error) {
            console.error('Error loading terms:', error);
        }
    };

    const handleSelectLocation = (location: CountryResponse) => {
        setFormValues(prev => ({ ...prev, locationId: location.id }))
        setSearchCountry(location.name);
        setIsCountryDropdownOpen(false);
    };

    const handleSelectSalePoint = (salePoint: any) => {
        setFormValues(prev => ({ ...prev, salePointFloorPlans: [{ salePointId: salePoint?.id, position: +formValues?.position || 1 }] }))
        setSearchSalePoint(salePoint?.salePointName);
        setIsSalePointDropdownOpen(false);
    };

    const handleChange = (e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>) => {
        const { name, value, type } = e.target;
        setFormValues({ ...formValues, [name]: value });
    };
    const handleFileChange = async (e: any) => {
        e.preventDefault();
        const file = e.target.files[0];
        if (file && (file.type.startsWith('image/') || file.type === 'application/pdf')) {
            setFileName(file.name);
            setFileUpload(file);
            try {
                const base64 = await convertToBase64(file);
                if (file.type.startsWith('image/')) {
                    setBase64Image(base64);
                    setBase64PDF(null);
                } else if (file.type === 'application/pdf') {
                    setBase64PDF(base64);
                    setBase64Image(null);
                }
            } catch (error) {
                console.error('Error converting file:', error);
            }
        } else {
            alert('Please upload an image or PDF file.');
        }
    };

    const handleDrop = async (e: any) => {
        e.preventDefault();
        setIsDragging(false);
        const file = e.dataTransfer.files[0];
        if (!file) return;

        if (file.type.startsWith('image/') || file.type === 'application/pdf') {
            setFileName(file.name);
            setFileUpload(file);
            try {
                const base64 = await convertToBase64(file);
                if (file.type.startsWith('image/')) {
                    setBase64Image(base64);
                    setBase64PDF(null);
                } else if (file.type === 'application/pdf') {
                    setBase64PDF(base64);
                    setBase64Image(null);
                }
            } catch (error) {
                console.error('Error converting file:', error);
            }
        } else {
            alert('Please upload a valid image or PDF file.');
        }
    };

    const convertToBase64 = (file: File): Promise<string> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => {
                resolve(reader.result as string);
            };
            reader.onerror = (error) => {
                reject(error);
            };
            reader.readAsDataURL(file);
        });
    };

    const handleDragOver = (e: any) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = () => {
        setIsDragging(false);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const formData = new FormData();
        console.log("🚀 ~ handleSubmit ~ fileUpload:", fileUpload)
        formData.append("image", fileUpload);
        // Update ignores blank fields, so the required rules only apply on create (every field listed).
        const limitFields = id === 'new' ? Object.keys(limits) : Object.keys(limits).filter((field) => String((formValues as any)[field] ?? '').trim() !== '');
        const newErrors: any = validateAgainstLimits(formValues, limits, language, limitFields);
        if (!formValues.planName) {
            newErrors.planName = 'Name is required';
        }
        if (formValues?.salePointFloorPlans[0]?.salePointId === '') {
            newErrors.salePointId = 'Sale Point is required';
        }
        setErrors(newErrors);
        if (Object.keys(newErrors).length > 0) {
            return;
        }
        webLoadingTrue();
        const payload: FloorPlanPayload = {
            ...formValues,
            position: +formValues.position,
            salePointFloorPlans: formValues?.salePointFloorPlans || [],
            floorPlanDetails: formValues?.floorPlanDetails.map((v) => {
                return {
                    name: v.name,
                    type: v.type,
                    shape: v.shape || 'image',
                    x: Math.round((v.x / window.innerWidth) * 100),
                    y: Math.round((v.y / window.innerHeight) * 100),
                    width: Math.round((v.width / window.innerWidth) * 100),
                    height: Math.round((v.height / window.innerHeight) * 100),
                    radius: +v.radius || 0,
                    rotation: +v.rotation || 0,
                    position: +v.position || 1,
                    maxCovers: +v.maxCovers || 1,
                    minCovers: +v.minCovers || 1,
                    isReserved: false,
                    reservedFrom: null,
                    reservedTo: null
                }
            }),
        };
        console.log("🚀 ~ handleSubmit ~ payload:", payload);
        formData.append("payload", JSON.stringify(payload));
        if (id === 'new') {
            try {
                const response: any = await createFloorPlan(formData);
                if (response) {
                    webNavigate('/' + preLanguage + '/floorPlan');
                    webLoadingFalse();
                    showToast({
                        message: "Floor Plan Created !",
                        type: 'success',
                        duration: 20000,
                    });
                }
            } catch (error: any) {
                webLoadingFalse();
                showApiError(error, { language, setFieldErrors: (fe) => setErrors((prev) => ({ ...prev, ...fe })) });
            }
        }
        else {
            try {
                const response: any = await updateFloorPlan(id, formData);
                if (response) {
                    webNavigate('/' + preLanguage + '/floorPlan');
                    webLoadingFalse();
                    showToast({
                        message: "Floor Plan Updated !",
                        type: 'success',
                        duration: 20000,
                    });
                }
            } catch (error: any) {
                webLoadingFalse();
                showApiError(error, { language, setFieldErrors: (fe) => setErrors((prev) => ({ ...prev, ...fe })) });
            }
        }
    };
    const handleRemoveFile = () => {
        setBase64Image('');
        setBase64PDF('');
        setFileName('');
        setFileUpload(null);
        setFormValues((prev: any) => ({ ...prev, image: null }));
        let inputHoolder = document.getElementById("fileInput") as HTMLInputElement;
        inputHoolder.value = '';
    };
    const handleClosePopupFloorPlan = () => {
        setShowPopupDraw(false);
    }

    const handleAddFloorPlan = (value: (FloorPlanDetailPayload | ImageShape)[]) => {
        console.log("🚀 ~ handleAddFloorPlan ~ value:", value)
        setFormValues(prev => ({ ...prev, floorPlanDetails: value as FloorPlanDetailPayload[] }))
        setShowPopupDraw(false);
    }

    const handleToggle = () => {
        setFormValues((prev) => ({
            ...prev,
            isActive: !prev.isActive
        }))
    };
    const handleChooseType = (type: string) => {
        setFormValues((prev) => ({
            ...prev,
            type: type
        }))
    }
    return (
        <>
            <div className="">
                <div className="flex items-center ">
                    <div
                        className="w-6 h-6 flex items-center justify-center cursor-pointer"
                        onClick={() => {
                            webNavigate('/' + preLanguage + '/floorPlan');
                        }}
                    >
                        <BackIcon />
                    </div>
                    <p
                        style={{ fontSize: '14px', fontWeight: '500', lineHeight: '28px', color: '#222525' }}
                        className="font-inter"
                    >
                        {language && language?.['Back'] ? language?.['Back'] : 'Back'}
                    </p>
                </div>
            </div>
            {id === 'new' ? (
                <p
                    style={{
                        margin: '20px 0px',
                        fontSize: '20px',
                        fontWeight: '500',
                        lineHeight: '28px',
                        color: '#222525',
                    }}
                    className="font-inter"
                >
                    {language && language?.['Add Floor Plan']
                        ? language?.['Add Floor Plan']
                        : 'Add Floor Plan'}
                </p>
            ) : (
                <p
                    style={{
                        margin: '20px 0px',
                        fontSize: '20px',
                        fontWeight: '500',
                        lineHeight: '28px',
                        color: '#222525',
                    }}
                    className="font-inter"
                >
                    {language && language?.['Edit Floor Plan']
                        ? language?.['Edit Floor Plan']
                        : 'Edit Floor Plan'}
                </p>
            )}
            <form className='overflow-scroll scroll-container' style={{ height: 'calc(100% - 163px)', paddingBottom: '24px' }}>
                <div
                    className="flex items-start"
                    style={{ flexWrap: 'wrap', gap: 24 }}
                >
                    <div className="space-y-2" style={{ width: isMobile ? "100%" : 'calc(50% - 12px)' }}>
                        <div className="flex items-center">
                            <p
                                className="text-[14px] font-inter leading-[20px]"
                                style={{ color: '#4A5353', fontWeight: '500' }}
                            >
                                {language && language?.['Name'] ? language?.['Name'] : 'Name'}
                            </p>
                            <p className="text-[14px] font-inter leading-[20px]" style={{ color: '#BA0E13' }}>
                                {language && language?.['*'] ? language?.['*'] : '*'}
                            </p>
                        </div>
                        <div className="w-full">
                            <input
                                tabIndex={1}
                                type="text"
                                name="planName"
                                {...limitProps(limits, 'planName')}
                                value={formValues.planName}
                                onChange={(e: any) => {
                                    handleChange(e);
                                }}
                                className={`text-[14px] font-inter placeholder:text-placeholder text-main border border-[#EBEBED] p-3 rounded-xl  outline-none focus-none bg-transparent w-full`}
                            />
                            <FieldCounter value={formValues.planName} max={limits.planName?.maxLength} />
                        </div>
                        {errors.planName && <p className="error">{errors.planName}</p>}
                    </div>
                    <div className="space-y-2" style={{ width: isMobile ? "100%" : 'calc(50% - 12px)' }}>
                        <div className="flex items-center justify-between">
                            <p
                                className="text-[14px] font-inter leading-[20px]"
                                style={{ color: '#4A5353', fontWeight: '500' }}
                            >
                                {language && language?.['Position'] ? language?.['Position'] : 'Position'}
                                <span style={{ color: '#819596' }}>
                                    {language && language?.[' (Numbers only)'] ? language?.[' (Numbers only)'] : ' (Numbers only)'}
                                </span>
                            </p>
                        </div>
                        <div className="w-full">
                            <input
                                tabIndex={2}
                                type="text"
                                name="position"
                                value={formValues.position}
                                onChange={(e: any) => {
                                    handleChange(e);
                                }}
                                onKeyDown={validateOnlyNumbersNotThree}
                                className={`text-[14px] font-inter placeholder:text-placeholder text-main border border-[#EBEBED] p-3 rounded-xl  outline-none focus-none bg-transparent w-full`}
                            />
                        </div>
                        {errors.position && <p className="error">{errors.position}</p>}
                    </div>
                    <div className="space-y-2" style={{ width: '100%' }}>
                        <div className="flex items-center justify-between">
                            <p
                                className="text-[14px] font-inter leading-[20px]"
                                style={{ color: '#4A5353', fontWeight: '500' }}
                            >
                                {language && language?.['Description'] ? language?.['Description'] : 'Description'}
                            </p>
                        </div>
                        <div className="w-full">
                            <textarea
                                style={{ height: "88px" }}
                                tabIndex={3}
                                name="description"
                                {...limitProps(limits, 'description')}
                                value={formValues.description}
                                onChange={(e: any) => {
                                    handleChange(e);
                                }}
                                onKeyDown={validateNoSpecialChars}
                                className={`text-[14px] font-inter placeholder:text-placeholder text-main border border-[#EBEBED] p-3 rounded-xl  outline-none focus-none bg-transparent w-full`}
                            />
                            <FieldCounter value={formValues.description} max={limits.description?.maxLength} />
                        </div>
                        {errors.description && <p className="error">{errors.description}</p>}
                    </div>
                    <div className="space-y-2" style={{ width: isMobile ? "100%" : 'calc(50% - 12px)' }}>
                        <div className="flex items-center justify-between">
                            <p className="text-[14px] font-inter leading-[20px]" style={{ color: "#4A5353", fontWeight: '500' }}>{language && language?.["Location"] ? language?.["Location"] : "Location"}</p>
                        </div>
                        <div className="w-full">
                            <DropdownSearch
                                refKey="location"
                                refs={{ location: dropdownCountryRef }}
                                openStates={{ location: isCountryDropdownOpen }}
                                open={(key) => key === "location" && setIsCountryDropdownOpen(true)}
                                close={(key) => key === "location" && setIsCountryDropdownOpen(false)}
                                data={country}
                                labelExtractor={(item) => item.name}
                                value={searchCountry}
                                setValue={setSearchCountry}
                                onSelect={handleSelectLocation}
                                error={errors.locationId}
                                tabIndex={4}
                                borderNone={false}
                            />
                        </div>
                    </div>

                    <div className="space-y-2" style={{ width: isMobile ? "100%" : 'calc(50% - 12px)' }}>
                        <div className="flex items-center">
                            <p className="text-[14px] font-inter leading-[20px]" style={{ color: "#4A5353", fontWeight: '500' }}>{language && language?.["Sale Point"] ? language?.["Sale Point"] : "Sale Point"}</p>
                            <p className="text-[14px] font-inter leading-[20px]" style={{ color: '#BA0E13' }}>
                                {language && language?.['*'] ? language?.['*'] : '*'}
                            </p>
                        </div>
                        <div className="w-full">
                            <DropdownSearch
                                refKey="salePoint"
                                refs={{ salePoint: dropdownSalePointRef }}
                                openStates={{ salePoint: isSalePointDropdownOpen }}
                                open={(key) => key === "salePoint" && setIsSalePointDropdownOpen(true)}
                                close={(key) => key === "salePoint" && setIsSalePointDropdownOpen(false)}
                                data={salePoint}
                                labelExtractor={(item) => item.salePointName}
                                value={searchSalePoint}
                                setValue={setSearchSalePoint}
                                onSelect={handleSelectSalePoint}
                                error={errors.salePointId}
                                tabIndex={5}
                                borderNone={false}
                            />
                        </div>
                    </div>

                    <div className="space-y-2" style={{ width: isMobile ? "100%" : 'calc(50% - 12px)', gap: '12px' }}>
                        <p className="text-[14px] font-inter leading-[20px]" style={{ color: "#4A5353", fontWeight: '500' }}>{language && language?.["Select sale point icon"] ? language?.["Select sale point icon"] : "Select sale point icon"}</p>
                        <div className="flex items-center gap-x-3">
                            <div onClick={() => { handleChooseType("bar") }} className="cursor-pointer flex items-center justify-center" style={{ width: '44px', height: '44px', borderRadius: '12px', border: formValues?.type === "bar" ? "1px solid #0F7679" : "1px solid #DBE4E4" }}>
                                <div className="flex items-center justify-center" style={{ width: '24px', height: '24px', }}>
                                    <BarIcon isActive={formValues?.type === "bar"} />
                                </div>
                            </div>
                            <div onClick={() => { handleChooseType("kitchen") }} className="cursor-pointer flex items-center justify-center" style={{ width: '44px', height: '44px', borderRadius: '12px', border: formValues?.type === "kitchen" ? "1px solid #0F7679" : "1px solid #DBE4E4" }}>
                                <div className="flex items-center justify-center" style={{ width: '24px', height: '24px', }}>
                                    <KitchenIcon isActive={formValues?.type === "kitchen"} />
                                </div>
                            </div>
                            <div onClick={() => { handleChooseType("dining") }} className="cursor-pointer flex items-center justify-center" style={{ width: '44px', height: '44px', borderRadius: '12px', border: formValues?.type === "dining" ? "1px solid #0F7679" : "1px solid #DBE4E4" }}>
                                <div className="flex items-center justify-center" style={{ width: '24px', height: '24px', }}>
                                    <DiningIcon isActive={formValues?.type === "dining"} />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="space-y-2" style={{ width: isMobile ? "100%" : 'calc(50% - 12px)' }}>
                        <p className="text-[14px] font-inter leading-[20px]" style={{ color: "#222525", fontWeight: '500' }}>{language && language?.["Status"] ? language?.["Status"] : "Status"}</p>
                        <div className="flex items-center gap-x-2">
                            <div className={`switch ${formValues?.isActive ? 'on' : 'off'}`} onClick={handleToggle}>
                                <div className={`shadow_toggle slider ${formValues?.isActive ? 'move-right' : 'move-left'}`}></div>
                            </div>
                        </div>
                        {errors.isActive && <p className="error">{errors.isActive}</p>}
                    </div>
                    {/*
                      FIN-1115: optional "Enable covers" dropdown.
                      Reuses DropdownSearch (the same component driving
                      Location and Sale Point above) — explicitly NOT a
                      bespoke control. The 50%-on-desktop / 100%-on-mobile
                      width comes from the shared isMobile pattern, so
                      there's nothing extra needed for the mobile layout.
                    */}
                    <div className="space-y-2" style={{ width: isMobile ? "100%" : 'calc(50% - 12px)' }}>
                        <div className="flex items-center justify-between">
                            <p className="text-[14px] font-inter leading-[20px]" style={{ color: "#4A5353", fontWeight: '500' }}>
                                {language && language?.["Enable covers"] ? language?.["Enable covers"] : "Enable covers"}
                            </p>
                        </div>
                        <div className="w-full">
                            <DropdownSearch
                                refKey="coversEnabled"
                                refs={{ coversEnabled: dropdownCoversRef }}
                                openStates={{ coversEnabled: isCoversDropdownOpen }}
                                open={(key) => key === "coversEnabled" && setIsCoversDropdownOpen(true)}
                                close={(key) => key === "coversEnabled" && setIsCoversDropdownOpen(false)}
                                data={coversOptions}
                                labelExtractor={(item) => item.name}
                                value={searchCovers}
                                setValue={setSearchCovers}
                                onSelect={(item) => {
                                    setFormValues(prev => ({ ...prev, coversEnabled: item.value }));
                                    setSearchCovers(item.name);
                                    setIsCoversDropdownOpen(false);
                                }}
                                tabIndex={6}
                                borderNone={false}
                            />
                        </div>
                    </div>
                    <div className="" style={{ width: "100%" }}>
                        <div className="flex items-center justify-between">
                            <p className="text-[14px] font-inter leading-[20px]" style={{ color: "#4A5353", fontWeight: '500' }}>{language && language?.["Background Image Upload"] ? language?.["Background Image Upload"] : "Background Image Upload"}</p>
                        </div>
                        <div
                            className="w-full flex items-center justify-center"
                            style={{
                                marginTop: "12px",
                                padding: "32px 24px",
                                backgroundColor: '#FFFFFF',
                                border: `1px dashed ${isDragging ? '#3AC0C4' : '#DBE4E4'}`,
                                borderRadius: '12px',
                                height: "164px"
                            }}
                            onDrop={handleDrop}
                            onDragOver={handleDragOver}
                            onDragLeave={handleDragLeave}
                        >
                            <div className="space-y-2 text-center">
                                <div className="flex items-center justify-center">
                                    <UploadIconV2 />
                                </div>


                                {base64Image || base64PDF || formValues?.image
                                    ?
                                    <div className="flex items-center justify-center" style={{ gap: "24px" }}>
                                        <p className="text-[14px] font-inter  font-semibold leading-[18px] cursor-pointer" style={{ color: "#0F7679", marginTop: "8px" }} onClick={() => { setShowPopupImage(true) }}>
                                            View Files
                                        </p>
                                        <p className="text-[14px] font-inter  font-semibold leading-[18px] cursor-pointer" style={{ color: "#0F7679", marginTop: "8px" }} onClick={() => { handleRemoveFile() }}>
                                            Remove FIle
                                        </p>
                                    </div>
                                    :
                                    <label htmlFor="fileInput" className="text-[14px] font-inter  font-semibold leading-[18px] cursor-pointer" style={{ color: "#0F7679", marginTop: "8px" }}>
                                        Upload Files
                                        {(!base64Image || !base64PDF || formValues?.image) &&
                                            <span className="text-[14px] text-main leading-[20px] font-inter" style={{ color: '#4A5353', marginLeft: "4px" }}>
                                                {fileName ? `File: ${fileName}` : (language && language?.["or drag & drop here"]) ? language?.["or drag & drop here"] : "or drag & drop here"}
                                            </span>
                                        }
                                    </label>
                                }
                                <p style={{ fontSize: '12px', color: '#6A7878' }} className="leading-[22px] font-inter">{language && language?.["PNG, JPG up to 10MB"] ? language?.["PNG, JPG up to 10MB"] : "PNG, JPG up to 10MB"}</p>

                                <input
                                    type="file"
                                    accept=".jpg,.jpeg,.png"
                                    onChange={handleFileChange}
                                    style={{ display: 'none' }}
                                    id="fileInput"
                                />
                            </div>
                        </div>
                    </div>
                    <div style={{ width: "100%" }}>
                        <div className="flex items-center justify-between">
                            <p className="text-[14px] font-inter leading-[20px]" style={{ color: "#4A5353", fontWeight: '500' }}>{language && language?.["Your tables"] ? language?.["Your tables"] : "Your tables"}</p>
                        </div>
                        <div className="flex items-center justify-center" style={{ width: "100%", marginTop: '12px', backgroundColor: '#F8F8F8', borderRadius: '16px', padding: "24px" }} >
                            <div className="">
                                <div className="flex items-center justify-center" >
                                    <ClickIcon />
                                </div>
                                {
                                    (formValues?.floorPlanDetails.length > 0) ?
                                        <div className="space-y-1 text-center" style={{ margin: '12px 0px' }}>
                                            <p style={{ color: '#222525', fontWeight: '600' }} className="text-[14px] text-main leading-[20px] font-inter">
                                                {formValues?.floorPlanDetails?.length} {language?.['table added'] ?? 'table added'}
                                            </p>
                                        </div>
                                        :
                                        <div className="space-y-1 text-center" style={{ margin: '12px 0px' }}>
                                            <p style={{ color: '#222525', fontWeight: '600' }} className="text-[14px] text-main leading-[20px] font-inter">
                                                {language?.['No tables yet'] ?? 'No tables yet'}
                                            </p>
                                            <p style={{ color: '#4A5353' }} className="text-[14px] leading-[20px] font-inter">
                                                {language?.['Start by adding tables to your workspace'] ?? 'Start by adding tables to your workspace'}
                                            </p>
                                        </div>

                                }
                                <div
                                    className="cursor-pointer flex items-center justify-center"
                                    style={{
                                        gap: '8px',
                                        padding: "8px 24px 8px 16px",
                                        backgroundColor: '#0F7679',
                                        borderRadius: '30px',
                                        width: 'fit-content',
                                        margin: '0 auto'
                                    }}
                                    onClick={() => { setShowPopupDraw(true) }}
                                >
                                    <div className="w-6 h-6 flex items-center justify-center">
                                        <PlusIcon />
                                    </div>
                                    <p
                                        style={{ color: '#FFFFFF' }}
                                        className="text-[14px] leading-[130%] font-medium font-inter"
                                    >
                                        {language && language?.['Add table'] ? language?.['Add table'] : 'Add table'}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </form>
            <div
                className={`absolute w-full h-full top-0 left-0 flex items-center justify-center px-4 ${showPopupImage ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"} transition-opacity duration-300 ease-in-out`}
                style={{ backgroundColor: "#22252552", zIndex: "200", transition: "opacity 0.3s ease, visibility 0.3s ease", width: "100%", padding: "16px" }}
            >
                <div
                    className={`transform transition-transform duration-300 ease-in-out ${showPopupImage ? "translate-y-0" : "translate-y-4 opacity-0"}`} style={{
                        borderRadius: "20px", backgroundColor: "white", width: "100%", height: "100%", transition: "transform 0.3s ease, opacity 0.3s ease", overflow: "hidden"
                    }}
                >
                    <div
                        className="flex items-center justify-between"
                    >
                        <p className="text-[16px] font-semibold font-inter text-main leading-[24px]">

                        </p>
                        <div
                            onClick={() => { setShowPopupImage(false); }}
                            className="flex items-center justify-center cursor-pointer"
                            style={{
                                width: "24px",
                                height: "24px",
                                borderRadius: "100%",
                                backgroundColor: "#D1E4E5",
                            }}
                        >
                            <CloseIcon />
                        </div>
                    </div>
                    <div className="flex items-center justify-center" style={{ padding: "20px 16px", height: "calc(100% - 48px)" }}>
                        {(base64PDF || base64Image || formValues?.image) && (
                            <>
                                {base64PDF ? (
                                    <iframe
                                        src={base64PDF}
                                        width="100%"
                                        height="100%"
                                        title="PDF Preview"
                                    />
                                ) : base64Image ? (
                                    <img
                                        src={base64Image}
                                        alt="Preview"
                                        style={{ width: "100%", height: "100%", objectFit: "contain" }}
                                    />
                                ) : formValues?.image ? (
                                    formValues.image.toLowerCase().endsWith(".pdf") ? (
                                        <iframe
                                            src={formValues.image}
                                            width="100%"
                                            height="100%"
                                            title="PDF Preview"
                                        />
                                    ) : (
                                        <img
                                            src={formValues.image}
                                            alt="Preview"
                                            style={{ width: "100%", height: "100%", objectFit: "contain" }}
                                        />
                                    )
                                ) : null}
                            </>
                        )}
                    </div>
                </div>
            </div>
            <div
                className="w-full flex items-center justify-end shadow_button relative"
                style={{ gap: "12px", paddingTop: "24px", }}
            >
                <div className="absolute" style={{ left: "-24px", top: 0, height: "1px", backgroundColor: "#DBE4E4", width: isMobile ? 'calc(100vw + 32px)' : "calc(-256px + 48px + 100vw)" }}></div>
                <div
                    onClick={() => { webNavigate("/" + preLanguage + '/floorPlan') }}
                    style={{
                        padding: '10px 24px',
                        borderRadius: '30px',
                        color: '#222525',
                        fontSize: '14px',
                        fontWeight: '500',
                        border: '1px solid #C9D7D8',
                    }}
                    className="font-inter cursor-pointer"
                >
                    {language && language?.['Cancel'] ? language?.['Cancel'] : 'Cancel'}
                </div>
                <div style={{
                    padding: '10px 24px',
                    borderRadius: '30px',
                    color: '#FFFFFF',
                    fontSize: '14px',
                    fontWeight: '500',
                    backgroundColor: '#0F7679',
                }}
                    className="font-inter cursor-pointer"
                    onClick={(e: any) => { handleSubmit(e) }}
                >
                    <p className="text-[14px] leading-[22px] font-inter">{language && language?.["Save"] ? language?.["Save"] : "Save"}</p>
                </div>
            </div>
            <div
                className={`absolute w-full h-full top-0 left-0 flex items-center justify-center px-4 ${showPopupDraw ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"} transition-opacity duration-300 ease-in-out`}
                style={{ backgroundColor: "#22252552", zIndex: "200", transition: "opacity 0.3s ease, visibility 0.3s ease", width: "100%", padding: "16px" }}
            >
                <DrawFloorPlan onClose={handleClosePopupFloorPlan} onSave={handleAddFloorPlan} listData={formValues?.floorPlanDetails as FloorPlanDetail[]} />
            </div>
        </>
    );
};
export default FloorPlan;
